import { Fragment, useCallback, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { playSoundLoop, Sound } from '~/assets/sounds';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { PanelControls } from '~/components/ui/PanelControls';
import { ShopContainer } from '~/components/ui/Shop';
import { Text } from '~/components/ui/Text';
import { ToggleSound } from '~/components/ui/ToggleSound';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction, InputKey } from '~/shared';
import { AppearanceTab } from '../../components/AppearanceTab';
import { DNATab } from '../../components/DNATab';
import { FacialHairTab } from '../../components/FacialHairTab';
import { FinishRegistration } from '../../components/FinishRegistration';
import { Head } from '../../components/Head';
import { MakeupTab } from '../../components/MakeupTab';
import {
	useHeadRotation,
	useRandomAppearance,
	useResetOverlays,
	useSaveCharacter,
} from '../../hooks';
import {
	setFaceMorphValues,
	setTabIndexNext,
	setTabIndexPrev,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	StyledPanelControlsContainer,
	StyledStepButtons,
} from './CharacterCreation.styled';

const tabs = [DNATab, AppearanceTab, FacialHairTab, MakeupTab];

const CharacterCreation: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const faceMorphConfigs = useObservable(
		rage.characterCreation.getFaceMorphConfigs(),
	);
	const modalState = useObservable(rage.modal.getCurrentModal());
	const tabIndex = useLayoutSelector(
		(state) => state.characterCreation.tabIndex,
	);
	const maxTabIndex = useLayoutSelector(
		(state) => state.characterCreation.maxTabIndex,
	);
	const inputActionsLocked = useLayoutSelector(
		(state) => state.characterCreation.inputActionsLocked,
	);
	const handleRandomAppearance = useRandomAppearance();
	const handleResetOverlays = useResetOverlays();
	const { handleSave } = useSaveCharacter();
	useHeadRotation();
	const stopSound = useMemo(() => playSoundLoop(Sound.CreateCharacter), []);

	const ActiveTab = tabs[tabIndex];

	const handleRandom = () => {
		handleRandomAppearance(true, true, true).catch(
			rage.notifications.showException,
		);
	};

	const handleBack = useCallback(() => {
		if (tabIndex === 0) {
			rage.characterCreation
				.exit()
				.catch((err) => rage.notifications.showException(err));
		} else {
			dispatch(setTabIndexPrev());
		}
	}, [tabIndex]);

	useInputAction(InputAction.CommonMenuNavLeft, () => {
		if (modalState) {
			return;
		}

		dispatch(setTabIndexPrev());
	});
	useInputAction(InputAction.CommonMenuNavRight, () => {
		if (modalState) {
			return;
		}

		dispatch(setTabIndexNext());
	});
	useInputAction(InputAction.CharacterCreationRandom, () => {
		if (inputActionsLocked || modalState) {
			return;
		}

		handleRandom();
	});

	const leftControls = [
		{
			text: 'Управление',
			keys: [InputKey.ArrowLeft, InputKey.ArrowRight],
		},
		{
			text: 'Сохранить персонажа',
			keys: [InputKey.Enter],
			onClick: handleSave,
		},
		{
			text: 'Случайная внешность',
			keys: [InputKey.R],
			onClick: handleRandom,
		},
		{
			text: 'Крутить камеру',
			keys: [InputKey.MouseRight],
		},
		{
			text: 'Крутить голову',
			keys: [InputKey.MouseLeft],
		},
	];

	useEffectOnce(() => {
		handleRandomAppearance(true, false, false)
			.then((gender) => {
				handleResetOverlays(gender).catch((err) =>
					rage.notifications.showException(err),
				);
			})
			.catch((err) => rage.notifications.showException(err));
		const newFaceMorphValues = faceMorphConfigs.map((item) => ({
			index: item.index,
			value: 0,
		}));

		dispatch(setFaceMorphValues(newFaceMorphValues));
		rage.characterCreation
			.setFaceMorphValues(newFaceMorphValues)
			.catch((err) => rage.notifications.showException(err));

		return () => {
			stopSound();
		};
	});

	useEffectOnce(() => {
		// Перетаскивание
		const handleDragging = (e: any) => {
			e.preventDefault();
		};
		document.addEventListener('dragstart', handleDragging);
		return () => document.removeEventListener('dragstart', handleDragging);
	});

	return (
		<Fragment>
			<ShopContainer data-head-rotation-ignore>
				<Head />
				<ActiveTab />
				<StyledStepButtons
					data-head-rotation-ignore
					$buttonCount={tabIndex === maxTabIndex ? 1 : 2}
				>
					<Button
						color={ButtonColors.Default}
						height={ButtonHeight.Height50}
						onClick={handleBack}
					>
						<Text>Назад</Text>
					</Button>
					{tabIndex < maxTabIndex && (
						<Button
							color={ButtonColors.Select}
							height={ButtonHeight.Height50}
							onClick={() => dispatch(setTabIndexNext())}
						>
							<Text>Продолжить</Text>
						</Button>
					)}
				</StyledStepButtons>
			</ShopContainer>
			<StyledPanelControlsContainer data-head-rotation-ignore>
				<PanelControls controls={leftControls} />
			</StyledPanelControlsContainer>
			<div data-head-rotation-ignore>
				<ToggleSound />
			</div>
			{tabIndex === maxTabIndex && <FinishRegistration />}
		</Fragment>
	);
};

export { CharacterCreation };
